import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Loader2, ShieldCheck, Award, Clock } from 'lucide-react';
export function QuoteForm() {
  const [formState, setFormState] = useState<'idle' | 'loading' | 'success'>('idle');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    message: '',
    consent: false
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormState('loading');
    setTimeout(() => setFormState('success'), 1500);
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };
  const inputClass = 'w-full bg-transparent border-0 border-b border-white/20 py-3 px-0 text-sm text-white placeholder:text-white/40 focus:ring-0 focus:border-accent transition-colors resize-none';
  return <section id="quote" className="w-full bg-primary-dark text-white py-24 overflow-hidden relative" data-id="element-422">
      {/* Subtle background decoration */}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-bl from-accent/5 to-transparent pointer-events-none" data-id="element-423"></div>

      <div className="container mx-auto px-6 relative z-10" data-id="element-424">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-start" data-id="element-425">
          {/* Left Column - Copy & Trust Signals */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="max-w-xl" data-id="element-426">
            <h2 className="text-5xl md:text-6xl font-serif leading-[1.1] tracking-tight mb-6" data-id="element-427">
              Request a <span className="text-accent italic" data-id="element-428">free quote</span>
            </h2>
            <p className="text-lg text-white/70 leading-relaxed mb-12" data-id="element-429">
              Get expert advice and a transparent, no-obligation quote for your
              commercial air conditioning and ventilation needs. We aim to
              respond within 4 working hours.
            </p>

            <div className="space-y-8" data-id="element-430">
              <div className="flex items-start gap-4" data-id="element-431">
                <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center shrink-0" data-id="element-432">
                  <Award className="w-6 h-6 text-accent" data-id="element-433" />
                </div>
                <div data-id="element-434">
                  <h4 className="text-lg font-medium mb-1" data-id="element-435">
                    Daikin D1+ Premier Partner
                  </h4>
                  <p className="text-sm text-white/50" data-id="element-436">
                    Access to extended warranties and premium support.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4" data-id="element-437">
                <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center shrink-0" data-id="element-438">
                  <ShieldCheck className="w-6 h-6 text-accent" data-id="element-439" />
                </div>
                <div data-id="element-440">
                  <h4 className="text-lg font-medium mb-1" data-id="element-441">
                    Constructionline Gold
                  </h4>
                  <p className="text-sm text-white/50" data-id="element-442">
                    Held by just 1-2% of HVAC firms in the UK.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4" data-id="element-443">
                <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center shrink-0" data-id="element-444">
                  <Clock className="w-6 h-6 text-accent" data-id="element-445" />
                </div>
                <div data-id="element-446">
                  <h4 className="text-lg font-medium mb-1" data-id="element-447">
                    16+ Years Experience
                  </h4>
                  <p className="text-sm text-white/50" data-id="element-448">
                    Trusted nationwide for installations built to last.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Column - Form */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 md:p-12" data-id="element-449">
            {formState === 'success' ? <div className="text-center py-16" data-id="element-450">
                <div className="w-20 h-20 bg-accent/20 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-451">
                  <ShieldCheck className="w-10 h-10 text-accent" data-id="element-452" />
                </div>
                <h3 className="text-3xl font-serif mb-4" data-id="element-453">Quote requested</h3>
                <p className="text-white/70" data-id="element-454">
                  Thank you. We've received your details and our team will be in
                  touch shortly to discuss your project.
                </p>
              </div> : <form className="space-y-8" onSubmit={handleSubmit} data-id="element-455">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-456">
                  <input id="firstName" name="firstName" type="text" placeholder="First name" required value={formData.firstName} onChange={handleChange} className={inputClass} data-id="element-457" />
                  <input id="lastName" name="lastName" type="text" placeholder="Last name" required value={formData.lastName} onChange={handleChange} className={inputClass} data-id="element-458" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-459">
                  <input id="email" name="email" type="email" placeholder="Email address" required value={formData.email} onChange={handleChange} className={inputClass} data-id="element-460" />
                  <input id="phone" name="phone" type="tel" placeholder="Phone number" required value={formData.phone} onChange={handleChange} className={inputClass} data-id="element-461" />
                </div>

                <input id="company" name="company" type="text" placeholder="Company name" required value={formData.company} onChange={handleChange} className={inputClass} data-id="element-462" />

                <textarea id="message" name="message" placeholder="Tell us about your project or requirements..." rows={3} value={formData.message} onChange={handleChange} className={inputClass} data-id="element-463" />

                <div className="flex items-start gap-3 pt-2" data-id="element-464">
                  <input type="checkbox" id="consent" name="consent" required checked={formData.consent} onChange={handleChange} className="mt-1 rounded-sm border-white/20 bg-transparent text-accent focus:ring-accent focus:ring-offset-primary-dark" data-id="element-465" />
                  <label htmlFor="consent" className="text-sm text-white/60" data-id="element-466">
                    I agree to the processing of my data in accordance with the{' '}
                    <a href="/privacy" className="text-white underline underline-offset-2 hover:text-accent transition-colors" data-id="element-467">
                      Privacy Policy
                    </a>
                    .
                  </label>
                </div>

                <div className="pt-4" data-id="element-468">
                  <button type="submit" disabled={formState === 'loading'} className="w-full sm:w-auto bg-accent hover:bg-accent/90 disabled:opacity-70 text-white text-sm font-medium py-4 px-10 rounded-full transition-colors flex items-center justify-center gap-3" data-id="element-469">
                    {formState === 'loading' ? <Loader2 className="w-5 h-5 animate-spin" data-id="element-470" /> : <span data-id="element-471">Submit request</span>}
                  </button>
                </div>
              </form>}
          </motion.div>
        </div>
      </div>
    </section>;
}